import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ChevronDown, Info } from "lucide-react";
import { sizeText } from "@/lib/sizes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Confidence -> compact colored dot + word (no bars). One line, minimal height.
const CONF = {
  High: { dot: "bg-emerald-500", text: "text-emerald-700" },
  Medium: { dot: "bg-amber-500", text: "text-amber-700" },
  Low: { dot: "bg-red-500", text: "text-red-600" },
  Unknown: { dot: "bg-neutral-300", text: "text-neutral-500" },
};

// Sizes always with fractions (42½, 45⅓): the backend's display strings first.
const euText = (rec) => sizeText(rec?.size_display, rec?.recommended_eu_size);
const ukText = (rec) => sizeText(rec?.size_uk_display, rec?.size_uk);
const usText = (rec) => sizeText(rec?.size_us_display, rec?.size_us);

// Build an ENGLISH "Why this size?" explanation from the structured fields the
// backend returns (its `reasoning` string is German, so we never render it).
function buildWhy(rec) {
  if (!rec) return "";
  const range = rec.matched_range_cm;
  const hasRange =
    Array.isArray(range) && range[0] != null && range[1] != null;
  if (rec.foot_length_cm != null && hasRange) {
    return `Your foot (${rec.foot_length_cm} cm) sits in this brand's ${range[0]}–${range[1]} cm bracket for EU ${euText(rec)}.`;
  }
  if (rec.foot_length_cm != null) {
    return `Estimated for EU ${euText(rec)} from your foot length (${rec.foot_length_cm} cm).`;
  }
  return `Recommended size EU ${euText(rec)}.`;
}

function ConfidenceInline({ conf, testid }) {
  const c = CONF[conf] || CONF.Unknown;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-[11.5px] ${c.text}`}
    >
      <span className={`h-2 w-2 rounded-full ${c.dot}`} />
      {conf || "Unknown"}
    </span>
  );
}

// Compact, embedded-friendly result widget.
//   - Headline: one primary brand size + inline confidence dot + "i" popover.
//   - Cross-brand list is COLLAPSED behind a "See your size in other brands"
//     toggle so the resting widget stays 1-2 lines (fits a PDP sidebar).
export default function BrandRecommendation({
  footLengthCm,
  footWidthCm = null,
  email = null,
  primaryBrand = null, // slug to feature as the headline (else first brand)
  limit = 4,
  className = "",
  testid = "brand-recommendation",
}) {
  const [brands, setBrands] = useState([]);
  const [rows, setRows] = useState([]); // [{ slug, name, rec }]
  const [loading, setLoading] = useState(false);
  const [showOthers, setShowOthers] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const whyRef = useRef(null);

  // Load the brand list once, keep the first `limit`.
  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/recommend/brands`)
      .then((r) => {
        if (mounted) setBrands((r.data.brands || []).slice(0, limit));
      })
      .catch(() => mounted && setBrands([]));
    return () => {
      mounted = false;
    };
  }, [limit]);

  // Resolve a recommendation for every brand whenever the foot length changes.
  useEffect(() => {
    if (!brands.length || !footLengthCm) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(
      brands.map((b) =>
        axios
          .post(`${API}/recommend`, {
            brand: b.slug,
            foot_length_cm: footLengthCm,
            foot_width_cm: footWidthCm || undefined,
            email: email || undefined,
          })
          .then((r) => ({ slug: b.slug, name: b.name, rec: r.data }))
          .catch(() => ({ slug: b.slug, name: b.name, rec: null }))
      )
    ).then((res) => {
      if (!cancelled) {
        setRows(res);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [brands, footLengthCm, footWidthCm, email]);

  // Close the "why" popover on outside click.
  useEffect(() => {
    if (!whyOpen) return;
    const onDoc = (e) => {
      if (whyRef.current && !whyRef.current.contains(e.target)) setWhyOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [whyOpen]);

  // Order rows so the featured brand is first.
  const ordered = useMemo(() => {
    if (!primaryBrand) return rows;
    const idx = rows.findIndex((r) => r.slug === primaryBrand);
    if (idx <= 0) return rows;
    const copy = [...rows];
    const [p] = copy.splice(idx, 1);
    return [p, ...copy];
  }, [rows, primaryBrand]);

  const primary = ordered[0];
  const others = ordered.slice(1);
  const isLoading = loading && rows.length === 0;

  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border border-black/10 bg-white p-4 ${className}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
        <Sparkles className="h-3 w-3" />
        Your Fittin size
      </div>

      {isLoading ? (
        <div data-testid={`${testid}-loading`} className="mt-3 space-y-2">
          <div className="h-8 w-40 rounded-lg bg-black/[0.04] animate-pulse" />
          <div className="h-3 w-24 rounded bg-black/[0.04] animate-pulse" />
        </div>
      ) : primary ? (
        <>
          {/* Primary headline \u2014 compact single line */}
          <div
            data-testid={`${testid}-${primary.slug}`}
            className="mt-3 flex items-center justify-between gap-3"
          >
            <div className="min-w-0">
              <div className="text-[11px] text-neutral-500 truncate">
                {primary.name}
              </div>
              <div className="flex items-baseline gap-2">
                {primary.rec && primary.rec.recommended_eu_size != null ? (
                  <span
                    data-testid={`${testid}-${primary.slug}-eu`}
                    className="text-3xl tracking-[-0.03em] font-medium leading-none"
                  >
                    EU {euText(primary.rec)}
                  </span>
                ) : (
                  <span
                    data-testid={`${testid}-${primary.slug}-empty`}
                    className="text-[13px] text-neutral-400"
                  >
                    No size data
                  </span>
                )}
                {primary.rec?.recommended_eu_size != null && (
                  <span className="text-[11px] font-mono text-neutral-500">
                    {primary.rec.size_uk != null && `UK ${ukText(primary.rec)}`}
                    {primary.rec.size_uk != null &&
                      primary.rec.size_us != null &&
                      " · "}
                    {primary.rec.size_us != null && `US ${usText(primary.rec)}`}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <ConfidenceInline
                conf={primary.rec?.confidence}
                testid={`${testid}-${primary.slug}-confidence`}
              />
              {/* "i" info popover: why + matched range + fit note */}
              <div className="relative" ref={whyRef}>
                <button
                  type="button"
                  data-testid={`${testid}-why-toggle`}
                  aria-label="Why this size?"
                  onClick={() => setWhyOpen((v) => !v)}
                  className="grid place-items-center h-6 w-6 rounded-full border border-black/10 text-neutral-500 hover:text-[#0A0A0A] hover:bg-neutral-50 transition-all"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
                <AnimatePresence>
                  {whyOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.98 }}
                      transition={{ duration: 0.16 }}
                      data-testid={`${testid}-why-popover`}
                      className="absolute right-0 top-8 z-20 w-64 rounded-xl border border-black/10 bg-white p-3.5 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)]"
                    >
                      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                        Why this size?
                      </div>
                      <p className="mt-1.5 text-[12px] text-neutral-700 leading-relaxed">
                        {buildWhy(primary.rec)}
                      </p>
                      {primary.rec?.fit_note && (
                        <p className="mt-2 text-[12px] text-neutral-500 leading-relaxed border-t border-black/5 pt-2">
                          {primary.rec.fit_note}
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Cross-brand list \u2014 collapsed by default */}
          {others.length > 0 && (
            <div className="mt-3 border-t border-black/5 pt-2">
              <button
                type="button"
                data-testid={`${testid}-others-toggle`}
                onClick={() => setShowOthers((v) => !v)}
                className="flex w-full items-center justify-between text-[12px] text-neutral-600 hover:text-[#0A0A0A] transition-colors py-1"
              >
                See your size in other brands
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    showOthers ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {showOthers && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    data-testid={`${testid}-others-list`}
                    className="overflow-hidden"
                  >
                    <div className="mt-1 divide-y divide-black/5">
                      {others.map(({ slug, name, rec }) => (
                        <div
                          key={slug}
                          data-testid={`${testid}-${slug}`}
                          className="flex items-center justify-between gap-3 py-2.5"
                        >
                          <div className="text-[13px] font-medium tracking-tight truncate">
                            {name}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {rec && rec.recommended_eu_size != null ? (
                              <span
                                data-testid={`${testid}-${slug}-eu`}
                                className="text-[15px] font-medium tabular-nums"
                              >
                                EU {euText(rec)}
                              </span>
                            ) : (
                              <span
                                data-testid={`${testid}-${slug}-empty`}
                                className="text-[12px] text-neutral-400"
                              >
                                &mdash;
                              </span>
                            )}
                            <ConfidenceInline
                              conf={rec?.confidence}
                              testid={`${testid}-${slug}-confidence`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </>
      ) : (
        <div data-testid={`${testid}-empty`} className="mt-3 text-[13px] text-neutral-400">
          Enter your foot length to see your size.
        </div>
      )}
    </div>
  );
}
